import { useCallback, useEffect, useRef, useState } from "react";
import {
  Archive,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Library,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import { api, ApiError } from "@/lib/api";
import {
  BODY_LABELS,
  BODY_REQUIRED_TYPES,
  MASTERMIND_CONTENT_TYPE_LABELS,
  MASTERMIND_SELECTABLE_CONTENT_TYPES,
  RESOURCE_STATUS_LABELS,
  RESOURCE_STATUSES,
  RESOURCE_TYPE_LABELS,
  RESOURCE_TYPES,
  RESOURCE_TYPES_WITH_CATEGORIES,
  TITLE_LABELS,
  createResourceCategory,
  deleteResourceCategory,
  fetchPublishableRoles,
  fetchResourceCategoriesForAdmin,
  formatFileSize,
  formatPublishedDate,
  formatRoleLabels,
  hasManagedCategories,
  isExternalOnlyContentType,
  mastermindThumbnail,
  openResourceFile,
  reorderResourceCategories,
  resourceReaderPath,
  updateResourceCategory,
  type PaginatedResources,
  type ResourceCategory,
  type ResourceContent,
  type ResourceRoleOption,
  type ResourceSourceType,
  type ResourceStatus,
  type ResourceType,
  type ResourceUploadResult,
} from "@/lib/resources";
import PublishConfirmationModal from "@/components/PublishConfirmationModal";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const PAGE_SIZE = 10;

const SELECT_CLASS =
  "rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";

const STATUS_PILL_CLASS: Record<ResourceStatus, string> = {
  published: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600",
  draft: "border-border/70 bg-muted/40 text-muted-foreground",
  archived: "border-amber-500/30 bg-amber-500/10 text-amber-600",
};

// ── Form ──────────────────────────────────────────────────────────────
interface FormState {
  resource_type: ResourceType;
  title: string;
  body: string;
  short_description: string;
  /** Free-text category — only used by the types without a managed list. */
  category: string;
  /** resource_categories.id, for the types that have one. "" means none. */
  category_id: string;
  content_type: string;
  source_type: ResourceSourceType;
  file_url: string;
  file_name: string;
  file_mime_type: string;
  file_size_bytes: number | null;
  external_url: string;
  thumbnail_url: string;
  display_order: string;
  status: ResourceStatus;
  published_at: string;
  /** roles.id values this resource is published to. */
  role_ids: number[];
}

const EMPTY_FORM: FormState = {
  resource_type: "glossary",
  title: "",
  body: "",
  short_description: "",
  category: "",
  category_id: "",
  content_type: "",
  source_type: "upload",
  file_url: "",
  file_name: "",
  file_mime_type: "",
  file_size_bytes: null,
  external_url: "",
  thumbnail_url: "",
  display_order: "0",
  status: "draft",
  published_at: "",
  role_ids: [],
};

function formFromResource(r: ResourceContent): FormState {
  return {
    resource_type: r.resource_type,
    title: r.title,
    body: r.body ?? "",
    short_description: r.short_description ?? "",
    category: r.category ?? "",
    category_id: r.category_id ?? "",
    content_type: r.content_type ?? "",
    source_type: r.source_type ?? "upload",
    file_url: r.file_url ?? "",
    file_name: r.file_name ?? "",
    file_mime_type: r.file_mime_type ?? "",
    file_size_bytes: r.file_size_bytes,
    external_url: r.external_url ?? "",
    thumbnail_url: r.thumbnail_url ?? "",
    display_order: String(r.display_order ?? 0),
    status: r.status,
    // <input type="date"> only accepts YYYY-MM-DD
    published_at: r.published_at ? r.published_at.slice(0, 10) : "",
    role_ids: r.role_ids ?? [],
  };
}

const isHttpUrl = (value: string) => /^https?:\/\//i.test(value.trim());

/**
 * Mirrors the server-side rules in app/schemas/resource_content.py so the admin
 * gets a precise message instead of a generic 422. The backend remains the
 * authority — this is a usability layer, not the security boundary.
 */
function validateForm(form: FormState): string | null {
  const type = form.resource_type;

  if (!form.title.trim()) return `${TITLE_LABELS[type]} is required`;
  if (BODY_REQUIRED_TYPES.includes(type) && !form.body.trim()) {
    return `${BODY_LABELS[type]} is required`;
  }

  if (type === "mastermind") {
    if (!form.content_type) return "Content Type is required";

    // Link-only types (Zoom Recording) skip the source question entirely and
    // must carry a usable URL. Falls through to the shared checks below rather
    // than returning early, so the publish/role rules still apply.
    if (isExternalOnlyContentType(form.content_type)) {
      const label = MASTERMIND_CONTENT_TYPE_LABELS[form.content_type] ?? "External";
      if (!form.external_url.trim() || !isHttpUrl(form.external_url)) {
        return `Please enter a valid ${label} URL.`;
      }
    } else {
      if (!form.source_type) return "Content Source is required";
      if (form.source_type === "upload" && !form.file_url) {
        return "Please upload a file, or switch the source to External URL";
      }
      if (form.source_type === "external" && !form.external_url.trim()) {
        return "External URL is required";
      }
    }
  }

  for (const [value, label] of [
    [form.external_url, "External URL"],
    [form.thumbnail_url, "Thumbnail"],
  ] as const) {
    if (value.trim() && !isHttpUrl(value)) {
      return `${label} must be a full URL starting with http:// or https://`;
    }
  }

  const order = Number(form.display_order);
  if (form.display_order !== "" && (Number.isNaN(order) || order < 0)) {
    return "Display order must be 0 or greater";
  }

  // Roles are checked last, after the content itself is valid — publishing
  // without an audience would put content live that nobody can reach. Drafts
  // may be saved with no roles chosen yet.
  if (form.status === "published" && form.role_ids.length === 0) {
    return "Please select at least one role for this resource.";
  }

  // Same rule for the managed category list: readers browse by category, so a
  // published entry filed under nothing would be unreachable that way. Drafts
  // may be parked without one.
  if (hasManagedCategories(type) && form.status === "published" && !form.category_id) {
    return "Please select a category for this resource.";
  }

  return null;
}

function toPayload(form: FormState) {
  const type = form.resource_type;
  const isMastermind = type === "mastermind";
  const usesUpload = isMastermind && form.source_type === "upload";

  return {
    resource_type: type,
    title: form.title.trim(),
    body: form.body.trim() || null,
    // Only news carries a card blurb.
    short_description: type === "news" ? form.short_description.trim() || null : null,
    // Exactly one of the two is ever sent: a managed type files by id (the
    // server derives the text), everything else keeps the free-text value.
    category: hasManagedCategories(type) ? null : form.category.trim() || null,
    category_id: hasManagedCategories(type) ? form.category_id || null : null,
    content_type: isMastermind ? form.content_type : null,
    source_type: isMastermind ? form.source_type : null,
    file_url: usesUpload ? form.file_url || null : null,
    file_name: usesUpload ? form.file_name || null : null,
    file_mime_type: usesUpload ? form.file_mime_type || null : null,
    file_size_bytes: usesUpload ? form.file_size_bytes : null,
    external_url: form.external_url.trim() || null,
    // Mastermind thumbnails are derived from the content type at render time
    // (resourceThumbnail), never stored — so they can't go stale on an edit.
    thumbnail_url: isMastermind ? null : form.thumbnail_url.trim() || null,
    display_order: Number(form.display_order) || 0,
    status: form.status,
    published_at: form.published_at || null,
    is_active: form.status !== "archived",
    role_ids: form.role_ids,
  };
}

/** Upload control shared by the file and thumbnail pickers. */
function UploadField({
  client,
  label,
  hint,
  kind,
  fileName,
  fileSize,
  onUploaded,
  onClear,
}: {
  client: ResourceApiClient;
  label: string;
  hint: string;
  kind: "file" | "thumbnail";
  fileName: string | null;
  fileSize?: number | null;
  onUploaded: (result: ResourceUploadResult) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset immediately so re-picking the same file still fires onChange.
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    try {
      const result = await client.upload<ResourceUploadResult>("/resources/upload", file, "file", {
        kind,
      });
      onUploaded(result);
      toast.success(`${file.name} uploaded`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {fileName ? (
        <div className="flex items-center gap-2 rounded-xl border border-border/80 bg-muted/20 px-3 py-2">
          <FileText className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate text-sm text-foreground">{fileName}</span>
          {fileSize ? (
            <span className="shrink-0 text-xs text-muted-foreground">{formatFileSize(fileSize)}</span>
          ) : null}
          <button
            type="button"
            onClick={onClear}
            aria-label={`Remove ${label}`}
            className="shrink-0 rounded p-1 text-muted-foreground/70 transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading ? "Uploading…" : "Choose File"}
          </Button>
          <span className="text-xs text-muted-foreground">{hint}</span>
        </div>
      )}
      <input ref={inputRef} type="file" className="hidden" onChange={handleChange} />
    </div>
  );
}

// ── Add / Edit modal ──────────────────────────────────────────────────
function ResourceFormModal({
  client,
  editing,
  roles,
  categories,
  onManageCategories,
  onClose,
  onSaved,
}: {
  client: ResourceApiClient;
  editing: ResourceContent | null;
  /** Publishing targets from GET /resources/roles — never hardcoded here. */
  roles: ResourceRoleOption[];
  /** Managed categories, for the types that use them. Includes deactivated
   *  ones so an entry already filed under one still shows its own value. */
  categories: ResourceCategory[];
  /** Opens the category manager on this form's resource type. */
  onManageCategories: (resourceType: ResourceType) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(
    editing ? formFromResource(editing) : { ...EMPTY_FORM },
  );
  const [saving, setSaving] = useState(false);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const toast = useToast();

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const type = form.resource_type;
  const isMastermind = type === "mastermind";
  const isNews = type === "news";
  // Zoom Recording (and any future link-only type) can't be an upload.
  const externalOnly = isMastermind && isExternalOnlyContentType(form.content_type);
  const usesManagedCategories = hasManagedCategories(type);

  // A deactivated category is not offered as a NEW choice, but the one this
  // entry is already filed under stays in the list — otherwise editing an old
  // entry would silently re-file it the moment the form loaded.
  const selectableCategories = categories.filter(
    (c) => c.resource_type === type && (c.is_active || c.id === form.category_id),
  );

  // True only when saving would newly make the resource visible: a new record
  // created as Published, or an existing Draft/Archived one flipped to
  // Published. Published -> Published and anything -> Draft stay one-click.
  const willPublish = form.status === "published" && editing?.status !== "published";

  /** The actual write. Reached directly for drafts, or via the confirmation. */
  const persist = async () => {
    // Second guard behind the disabled buttons so a double-fire can't create
    // two records.
    if (saving) return;

    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing) {
        await client.put<ResourceContent>(`/resources/${editing.id}`, payload);
      } else {
        await client.post<ResourceContent>("/resources", payload);
      }
      setConfirmPublishOpen(false);
      toast.success(
        willPublish
          ? "Resource published successfully."
          : `${payload.title} ${editing ? "updated" : "created"}`,
      );
      onSaved();
    } catch (err) {
      // Both dialogs stay open and nothing is reported as published — only a
      // confirmed success response closes them.
      toast.error(
        err instanceof ApiError ? err.message : "Failed to save. Please check the form and try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  /** Save button: validate first, then confirm only if this will publish. */
  const handleSubmit = () => {
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    if (willPublish) {
      setConfirmPublishOpen(true);
      return;
    }
    void persist();
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      {/* max-w-3xl (768px) — one step up the same Tailwind scale the other form
          modals use, so the added Resource fields need less vertical scrolling.
          The p-4 on the backdrop keeps safe gutters on mobile, where the panel
          goes near-full width. */}
      <div className="flex max-h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header — semantic tokens only (bg-muted / text-foreground /
            text-muted-foreground). The raw Tailwind slate scale and `white` are
            remapped by the dark-theme block in index.css, so a raw pairing like
            `from-slate-800 … text-white` doesn't keep its contrast across themes;
            these token pairs are defined as matched fg/bg in both. */}
        <div className="shrink-0 border-b border-border bg-muted px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-bold leading-snug text-foreground">
                {editing ? "Edit Resource" : "Add Resource"}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {editing
                  ? RESOURCE_TYPE_LABELS[editing.resource_type]
                  : "Create a resource by filling in the details below."}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="space-y-1.5">
            <Label className="text-xs">Resource Type *</Label>
            <select
              className={`${SELECT_CLASS} w-full`}
              value={form.resource_type}
              // Changing type on an existing record would orphan type-specific
              // fields, so it is locked once saved.
              disabled={!!editing}
              onChange={(e) => set("resource_type", e.target.value as ResourceType)}
            >
              {RESOURCE_TYPES.map((t) => (
                <option key={t} value={t}>{RESOURCE_TYPE_LABELS[t]}</option>
              ))}
            </select>
            {editing && (
              <p className="text-xs text-muted-foreground">
                Resource type can't be changed after creation.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{TITLE_LABELS[type]} *</Label>
            <Input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder={type === "glossary" ? "e.g. EBITDA" : "e.g. How do I contact an advisor?"}
            />
          </div>

          {isNews && (
            <div className="space-y-1.5">
              <Label className="text-xs">Short Description</Label>
              <Textarea
                className="min-h-[70px]"
                value={form.short_description}
                onChange={(e) => set("short_description", e.target.value)}
                placeholder="One or two lines shown on the article card."
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">
              {BODY_LABELS[type]}
              {BODY_REQUIRED_TYPES.includes(type) ? " *" : ""}
            </Label>
            <Textarea
              className={isNews ? "min-h-[180px]" : "min-h-[120px]"}
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
            />
          </div>

          {isMastermind && (
            <>
              {/* Content type and the thumbnail it drives sit side by side —
                  they belong together and it saves a row of scrolling. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Content Type *</Label>
                  <select
                    className={`${SELECT_CLASS} w-full`}
                    value={form.content_type}
                    // Switching to an external-only type (Zoom Recording,
                    // External Link) also switches the source, so the admin
                    // never has to.
                    onChange={(e) => {
                      const next = e.target.value;
                      setForm((f) => ({
                        ...f,
                        content_type: next,
                        source_type: isExternalOnlyContentType(next) ? "external" : f.source_type,
                      }));
                    }}
                  >
                    <option value="">Select…</option>
                    {MASTERMIND_SELECTABLE_CONTENT_TYPES.map((c) => (
                      <option key={c} value={c}>{MASTERMIND_CONTENT_TYPE_LABELS[c]}</option>
                    ))}
                  </select>
                </div>

                {/* Derived from the content type — nothing to upload, so this is
                    a preview of what will be used. */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Thumbnail</Label>
                  <div className="flex items-center gap-3 rounded-xl border border-border/80 bg-muted/20 p-2.5">
                    <img
                      src={mastermindThumbnail(form.content_type)}
                      alt=""
                      className="h-11 w-[4.5rem] shrink-0 rounded-lg object-cover"
                    />
                    <p className="text-xs leading-snug text-muted-foreground">
                      Assigned automatically from the content type.
                    </p>
                  </div>
                </div>
              </div>

              {/* Source picker is hidden for content types that can only ever be
                  a link — there is nothing to choose. */}
              {externalOnly ? (
                <p className="text-xs text-muted-foreground">
                  {MASTERMIND_CONTENT_TYPE_LABELS[form.content_type] ?? "This content type"} is always
                  linked, never uploaded — paste the share URL below.
                </p>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs">Content Source *</Label>
                  <div className="flex flex-wrap gap-4">
                    {([
                      ["upload", "Upload File"],
                      ["external", "External URL"],
                    ] as const).map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="radio"
                          name="source_type"
                          checked={form.source_type === value}
                          onChange={() => set("source_type", value)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use External URL for a hosted recording — no need to upload the video.
                  </p>
                </div>
              )}

              {form.source_type === "upload" && !externalOnly ? (
                <UploadField
                  client={client}
                  label="File *"
                  hint="PDF, Office docs, CSV, MP4/MOV or images — max 50 MB"
                  kind="file"
                  fileName={form.file_name || null}
                  fileSize={form.file_size_bytes}
                  onUploaded={(r) =>
                    setForm((f) => ({
                      ...f,
                      file_url: r.file_url,
                      file_name: r.file_name,
                      file_mime_type: r.file_mime_type,
                      file_size_bytes: r.file_size_bytes,
                    }))
                  }
                  onClear={() =>
                    setForm((f) => ({
                      ...f,
                      file_url: "",
                      file_name: "",
                      file_mime_type: "",
                      file_size_bytes: null,
                    }))
                  }
                />
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    {externalOnly
                      ? `${MASTERMIND_CONTENT_TYPE_LABELS[form.content_type] ?? "External"} URL *`
                      : "URL *"}
                  </Label>
                  <Input
                    value={form.external_url}
                    onChange={(e) => set("external_url", e.target.value)}
                    placeholder={
                      form.content_type === "zoom_recording"
                        ? "https://zoom.us/rec/share/…"
                        : "https://example.com/resource"
                    }
                  />
                </div>
              )}

            </>
          )}

          {isNews && (
            <div className="space-y-1.5">
              <Label className="text-xs">External Article URL</Label>
              <Input
                value={form.external_url}
                onChange={(e) => set("external_url", e.target.value)}
                placeholder="https://example.com/article"
              />
            </div>
          )}

          {/* Manual thumbnail upload is for News only — Mastermind derives its
              thumbnail from the content type (see the preview above). */}
          {isNews && (
            <UploadField
              client={client}
              label="Thumbnail"
              hint="JPG, PNG, WebP or GIF — max 5 MB"
              kind="thumbnail"
              fileName={form.thumbnail_url ? form.thumbnail_url.split("/").pop() ?? "Thumbnail" : null}
              onUploaded={(r) => set("thumbnail_url", r.file_url)}
              onClear={() => set("thumbnail_url", "")}
            />
          )}

          {/* Available For — the roles this resource is published to. Options
              come from the API (roles table, filtered server-side), so a role
              that isn't enabled for publishing yet simply never appears. */}
          <div className="space-y-2">
            <Label className="text-xs">
              Available For{form.status === "published" ? " *" : ""}
            </Label>
            {roles.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No publishing roles are currently available.
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {roles.map((role) => {
                  const checked = form.role_ids.includes(role.id);
                  return (
                    <label key={role.id} className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setForm((f) => ({
                            ...f,
                            role_ids: checked
                              ? f.role_ids.filter((id) => id !== role.id)
                              : [...f.role_ids, role.id],
                          }))
                        }
                      />
                      {role.label}
                    </label>
                  );
                })}
                <button
                  type="button"
                  onClick={() => set("role_ids", roles.map((r) => r.id))}
                  className="text-xs font-semibold text-primary underline-offset-2 hover:underline"
                >
                  Select all available roles
                </button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Only the selected roles will see this content once it is published.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs">Category</Label>
                {usesManagedCategories && (
                  <button
                    type="button"
                    onClick={() => onManageCategories(type)}
                    className="text-xs font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    Manage
                  </button>
                )}
              </div>
              {usesManagedCategories ? (
                <>
                  <select
                    className={`${SELECT_CLASS} w-full`}
                    value={form.category_id}
                    onChange={(e) => set("category_id", e.target.value)}
                  >
                    <option value="">Select a category…</option>
                    {selectableCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.is_active ? "" : " (inactive)"}
                      </option>
                    ))}
                  </select>
                  {selectableCategories.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No {RESOURCE_TYPE_LABELS[type]} categories yet — use Manage to add the first one.
                    </p>
                  )}
                </>
              ) : (
                <Input
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  placeholder="e.g. Financing"
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Display Order</Label>
              <Input
                type="number"
                min={0}
                value={form.display_order}
                onChange={(e) => set("display_order", e.target.value)}
              />
            </div>
            {(isNews || isMastermind) && (
              <div className="space-y-1.5">
                <Label className="text-xs">Publish Date</Label>
                <Input
                  type="date"
                  value={form.published_at}
                  onChange={(e) => set("published_at", e.target.value)}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <select
                className={`${SELECT_CLASS} w-full`}
                value={form.status}
                onChange={(e) => set("status", e.target.value as ResourceStatus)}
              >
                {RESOURCE_STATUSES.map((s) => (
                  <option key={s} value={s}>{RESOURCE_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-muted/20 px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : editing ? "Save Changes" : "Create Resource"}
          </Button>
        </div>
      </div>
    </div>

    {/* Same confirmation component the list-view Publish action uses. Rendered
        as a sibling (not nested) so it layers above this dialog; cancelling it
        leaves the form untouched with every entered value intact. */}
    <PublishConfirmationModal
      isOpen={confirmPublishOpen}
      resourceName={form.title.trim() || undefined}
      resourceType={RESOURCE_TYPE_LABELS[form.resource_type]}
      currentStatus={editing ? RESOURCE_STATUS_LABELS[editing.status] : "New resource"}
      targetAudience={formatRoleLabels(form.role_ids, roles)}
      confirmLabel={editing ? "Publish" : "Create & Publish"}
      isLoading={saving}
      onConfirm={persist}
      onCancel={() => { if (!saving) setConfirmPublishOpen(false); }}
    />
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────
/**
 * The client the page talks to. The portal `api` and the super admin's
 * `superAdminApi` both satisfy it — they differ only in which token they send
 * and where they bounce on a 401, which is exactly why the super admin console
 * cannot just import `api` (it would send the portal token and, on 401, throw
 * the super admin out to the portal sign-in page).
 */
/**
 * Read-only preview of one resource.
 *
 * The admin grid can only ever show a title and a few pills, and the edit form
 * is the wrong place to *read* something — it is a wall of inputs, and opening
 * it to check a definition risks saving a stray keystroke. This shows the whole
 * record as content: body text, the attachment, the external link, and the
 * publishing metadata that decides who actually sees it.
 */
function ResourceViewModal({
  resource,
  roles,
  showReaderLink,
  onClose,
  onEdit,
}: {
  resource: ResourceContent;
  roles: ResourceRoleOption[];
  /** Only in the portal console. The super admin has no portal session, so a
   *  link into the reader pages would bounce them to the portal sign-in. */
  showReaderLink: boolean;
  onClose: () => void;
  onEdit: () => void;
}) {
  const toast = useToast();
  const [opening, setOpening] = useState(false);

  // The blob is streamed through an authenticated endpoint, so this can't be a
  // plain <a href> — see openResourceFile.
  const openFile = async (download: boolean) => {
    setOpening(true);
    try {
      await openResourceFile(resource, { download });
    } catch {
      toast.error("Could not open that file.");
    } finally {
      setOpening(false);
    }
  };

  const readerPath = resourceReaderPath(resource);

  const facts: { label: string; value: string }[] = [
    { label: "Resource type", value: RESOURCE_TYPE_LABELS[resource.resource_type] },
    { label: "Available for", value: formatRoleLabels(resource.role_ids, roles) },
    { label: "Publish date", value: formatPublishedDate(resource.published_at) },
    { label: "Category", value: resource.category?.trim() || "—" },
    ...(resource.content_type
      ? [{
          label: "Content type",
          value: MASTERMIND_CONTENT_TYPE_LABELS[resource.content_type] ?? resource.content_type,
        }]
      : []),
    { label: "Display order", value: String(resource.display_order) },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header — semantic token pairs only, so contrast survives both themes. */}
        <div className="shrink-0 border-b border-border bg-muted px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  {RESOURCE_TYPE_LABELS[resource.resource_type]}
                </p>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[0.62rem] font-semibold ${STATUS_PILL_CLASS[resource.status]}`}>
                  {RESOURCE_STATUS_LABELS[resource.status]}
                </span>
              </div>
              <p className="mt-1 font-display text-lg font-bold leading-snug text-foreground">
                {resource.title}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {resource.thumbnail_url && (
            <img
              src={resource.thumbnail_url}
              alt=""
              className="max-h-52 w-full rounded-xl border border-border/70 object-cover"
            />
          )}

          <dl className="grid gap-3 sm:grid-cols-2">
            {facts.map((f) => (
              <div key={f.label} className="rounded-xl border border-border/70 px-3 py-2.5">
                <dt className="text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  {f.label}
                </dt>
                <dd className="mt-0.5 text-sm text-foreground">{f.value}</dd>
              </div>
            ))}
          </dl>

          {resource.short_description && (
            <section>
              <p className="text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Short description
              </p>
              <p className="mt-1.5 text-sm leading-6 text-foreground/90">{resource.short_description}</p>
            </section>
          )}

          {resource.body && (
            <section>
              <p className="text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                {BODY_LABELS[resource.resource_type]}
              </p>
              <p className="mt-1.5 whitespace-pre-line text-sm leading-6 text-foreground/90">
                {resource.body}
              </p>
            </section>
          )}

          {resource.external_url && (
            <a
              href={resource.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 rounded-xl border border-border/70 px-3 py-2.5 text-sm transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <ExternalLink className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate text-primary">{resource.external_url}</span>
            </a>
          )}

          {resource.has_file && (
            <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-border/70 bg-muted/20 px-3 py-2.5">
              <FileText className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                {resource.file_name ?? "Attached file"}
              </span>
              {resource.file_size_bytes ? (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatFileSize(resource.file_size_bytes)}
                </span>
              ) : null}
              <Button size="sm" variant="outline" disabled={opening} onClick={() => void openFile(false)}>
                <Eye className="mr-1 h-3.5 w-3.5" />
                Open
              </Button>
              <Button size="sm" variant="outline" disabled={opening} onClick={() => void openFile(true)}>
                <Download className="mr-1 h-3.5 w-3.5" />
                Download
              </Button>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/40 px-6 py-4">
          {/* The reader page, as buyers and advisors see it. News has a
              per-article page an admin can open at any status. The other
              libraries are one list page of published rows, so a draft has
              nowhere to land and the reason is shown instead. */}
          {showReaderLink && (readerPath ? (
            <a
              href={readerPath}
              target="_blank"
              rel="noopener noreferrer"
              className="mr-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary underline-offset-2 hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              Open on site
            </a>
          ) : (
            <span className="mr-auto text-xs text-muted-foreground">
              Publish it to see it on the {RESOURCE_TYPE_LABELS[resource.resource_type]} page.
            </span>
          ))}
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={onEdit}>
            <Pencil className="mr-1 h-4 w-4" />
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Category manager ──────────────────────────────────────────────────
/**
 * Add / rename / reorder / retire the categories one library files its entries
 * under. Reachable from the page header and from the form's Category field, so
 * a missing category can be created without abandoning a half-filled entry.
 *
 * Deactivating is the safe default and deletion is refused server-side while a
 * category is still in use — the counts shown here are what that refusal is
 * based on, so the admin can see it coming.
 */
function CategoryManagerModal({
  client,
  resourceType,
  onResourceTypeChange,
  onClose,
  onChanged,
}: {
  client: ResourceApiClient;
  resourceType: ResourceType;
  /** Switch the manager to another library that has categories. */
  onResourceTypeChange: (resourceType: ResourceType) => void;
  onClose: () => void;
  /** Fired after any successful write so the page and the open form reload. */
  onChanged: (categories: ResourceCategory[]) => void;
}) {
  const [categories, setCategories] = useState<ResourceCategory[] | null>(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  // Read through a ref, not listed as a dependency: the parent passes a fresh
  // arrow every render, and calling it re-renders the parent — so depending on
  // it re-created `load`, re-fired the effect below, and refetched in a loop.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(() => {
    fetchResourceCategoriesForAdmin(resourceType, client)
      .then((rows) => {
        setCategories(rows);
        onChangedRef.current(rows);
      })
      .catch(() => toast.error("Failed to load categories."));
  }, [client, resourceType, toast]);

  useEffect(() => { load(); }, [load]);

  /** Every write funnels through here so the busy flag, the error toast and
   *  the reload are applied identically and can't drift apart. */
  const run = async (action: () => Promise<unknown>, success: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      toast.success(success);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Category name is required");
      return;
    }
    void run(
      async () => {
        await createResourceCategory(
          {
            name,
            description: null,
            // Append: one step past the current last, leaving the gaps the
            // reorder endpoint relies on.
            display_order: (categories?.length ?? 0) * 10,
            is_active: true,
          },
          resourceType,
          client,
        );
        setNewName("");
      },
      `"${name}" added`,
    );
  };

  const handleRename = (category: ResourceCategory) => {
    const name = draftName.trim();
    if (!name) {
      toast.error("Category name is required");
      return;
    }
    if (name === category.name) {
      setEditingId(null);
      return;
    }
    void run(
      async () => {
        await updateResourceCategory(
          category.id,
          {
            name,
            description: category.description,
            display_order: category.display_order,
            is_active: category.is_active,
          },
          client,
        );
        setEditingId(null);
      },
      "Category renamed",
    );
  };

  const handleToggleActive = (category: ResourceCategory) =>
    run(
      () =>
        updateResourceCategory(
          category.id,
          {
            name: category.name,
            description: category.description,
            display_order: category.display_order,
            is_active: !category.is_active,
          },
          client,
        ),
      category.is_active
        ? `"${category.name}" hidden from the pickers`
        : `"${category.name}" is available again`,
    );

  const handleDelete = (category: ResourceCategory) => {
    if (!window.confirm(`Delete the "${category.name}" category? This cannot be undone.`)) return;
    void run(() => deleteResourceCategory(category.id, client), `"${category.name}" deleted`);
  };

  /** Swap with the neighbour and persist the whole order — the endpoint
   *  renumbers from the list it is given. */
  const handleMove = (index: number, delta: number) => {
    if (!categories) return;
    const target = index + delta;
    if (target < 0 || target >= categories.length) return;

    const reordered = [...categories];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setCategories(reordered);
    void run(
      () => reorderResourceCategories(reordered.map((c) => c.id), resourceType, client),
      "Order updated",
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="flex max-h-[85dvh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="shrink-0 border-b border-border bg-muted px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-bold leading-snug text-foreground">
                {RESOURCE_TYPE_LABELS[resourceType]} Categories
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Entries are filed under these, and readers browse by them.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {RESOURCE_TYPES_WITH_CATEGORIES.length > 1 && (
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Library">
              {RESOURCE_TYPES_WITH_CATEGORIES.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={t === resourceType}
                  disabled={busy}
                  onClick={() => { if (t !== resourceType) onResourceTypeChange(t); }}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                    t === resourceType
                      ? "border-primary/50 bg-primary/10 text-primary"
                      : "border-border/70 text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  {RESOURCE_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
              placeholder="New category name, e.g. Financing"
            />
            <Button type="button" onClick={handleAdd} disabled={busy}>
              <Plus className="mr-1 h-4 w-4" />
              Add
            </Button>
          </div>

          {categories === null ? (
            <div className="flex justify-center py-8"><Loader /></div>
          ) : categories.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No categories yet. Add the first one above.
            </p>
          ) : (
            <ul className="space-y-2">
              {categories.map((c, index) => (
                <li
                  key={c.id}
                  className={`flex items-center gap-2 rounded-xl border border-border px-3 py-2 ${
                    c.is_active ? "bg-card/60" : "bg-muted/40"
                  }`}
                >
                  <div className="flex shrink-0 flex-col">
                    <button
                      type="button"
                      aria-label="Move up"
                      disabled={busy || index === 0}
                      onClick={() => handleMove(index, -1)}
                      className="text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Move down"
                      disabled={busy || index === categories.length - 1}
                      onClick={() => handleMove(index, 1)}
                      className="text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>

                  {editingId === c.id ? (
                    <Input
                      autoFocus
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRename(c);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={() => handleRename(c)}
                      className="h-8"
                    />
                  ) : (
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {c.name}
                        {!c.is_active && (
                          <span className="ml-2 text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground">
                            Hidden
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {c.entry_count} {c.entry_count === 1 ? "entry" : "entries"} · /{c.slug}
                      </p>
                    </div>
                  )}

                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      aria-label="Rename"
                      disabled={busy}
                      onClick={() => { setEditingId(c.id); setDraftName(c.name); }}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={c.is_active ? "Hide from pickers" : "Make available again"}
                      disabled={busy}
                      onClick={() => void handleToggleActive(c)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
                    >
                      {c.is_active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      type="button"
                      aria-label="Delete"
                      // Deleting one that is in use is refused server-side; the
                      // button stays enabled so the admin gets that explanation
                      // rather than an unexplained dead control.
                      disabled={busy}
                      onClick={() => handleDelete(c)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="shrink-0 border-t border-border px-6 py-4">
          <p className="text-xs text-muted-foreground">
            Hiding a category keeps existing entries where they are but removes it
            from the picker and the reader-facing filter.
          </p>
        </div>
      </div>
    </div>
  );
}

export interface ResourceApiClient {
  get: <T>(url: string) => Promise<T>;
  post: <T>(url: string, data: unknown) => Promise<T>;
  put: <T>(url: string, data: unknown) => Promise<T>;
  patch: <T>(url: string, data: unknown) => Promise<T>;
  delete: <T>(url: string) => Promise<T>;
  upload: <T>(url: string, file: File, fieldName?: string, fields?: Record<string, string>) => Promise<T>;
}

export default function AdminResourceContentPage({ client = api }: { client?: ResourceApiClient } = {}) {
  const [data, setData] = useState<PaginatedResources | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  // Publishing targets, loaded once. Server-side this is the roles table
  // filtered by PUBLISHABLE_ROLE_NAMES, so Seller appears here automatically
  // once it is enabled — no frontend change needed.
  const [roles, setRoles] = useState<ResourceRoleOption[]>([]);
  // Managed categories for every type that has them, in one flat list (the
  // form filters by type). Loaded through the admin route so deactivated ones
  // are included — an entry already filed under one must keep showing it in
  // the form's picker.
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  // Which library the category manager is open on, or null when closed.
  const [categoryManagerType, setCategoryManagerType] = useState<ResourceType | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"" | ResourceType>("");
  const [statusFilter, setStatusFilter] = useState<"" | ResourceStatus>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ResourceContent | null>(null);
  // Publishing is confirmed first — the row the admin is about to make live,
  // plus an in-flight flag that blocks duplicate submits.
  const [publishTarget, setPublishTarget] = useState<ResourceContent | null>(null);
  const [publishing, setPublishing] = useState(false);
  // Read-only preview. Separate from `editing` so opening a resource to read it
  // can never turn into an accidental save.
  const [viewing, setViewing] = useState<ResourceContent | null>(null);
  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (appliedSearch.trim()) params.set("search", appliedSearch.trim());
    if (typeFilter) params.set("resource_type", typeFilter);
    if (statusFilter) params.set("status", statusFilter);

    client.get<PaginatedResources>(`/resources/admin?${params}`)
      .then(setData)
      .catch(() => toast.error("Failed to load resources."))
      .finally(() => setLoading(false));

    client.get<{ counts: Record<string, number> }>("/resources/admin/summary")
      .then((d) => setCounts(d.counts))
      .catch(() => {});
  }, [page, appliedSearch, typeFilter, statusFilter, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchPublishableRoles(client)
      .then(setRoles)
      .catch(() => toast.error("Failed to load publishing roles."));
  }, [client, toast]);

  useEffect(() => {
    // A failed load is not fatal — the rest of the screen still works, the
    // picker is just empty, so it deliberately does not block with an error.
    Promise.all(RESOURCE_TYPES_WITH_CATEGORIES.map((t) => fetchResourceCategoriesForAdmin(t, client)))
      .then((lists) => setCategories(lists.flat()))
      .catch(() => setCategories([]));
  }, [client]);

  // The manager reports one type's list. Swap just that type's rows so the
  // other libraries' categories stay loaded for the form.
  const replaceCategories = useCallback((resourceType: ResourceType, rows: ResourceCategory[]) => {
    setCategories((prev) => [...prev.filter((c) => c.resource_type !== resourceType), ...rows]);
  }, []);

  // Any filter change restarts at page 1, so a narrow result set can't leave the
  // grid stranded on a page that no longer exists.
  useEffect(() => { setPage(1); }, [appliedSearch, typeFilter, statusFilter]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasFilters = !!(appliedSearch || typeFilter || statusFilter);

  const applySearch = () => { setAppliedSearch(search); setPage(1); };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setTypeFilter("");
    setStatusFilter("");
  };

  // Unpublish / archive — reversible and they only ever reduce visibility, so
  // they stay one-click. Publishing goes through confirmPublish below.
  const changeStatus = async (resource: ResourceContent, status: ResourceStatus) => {
    try {
      await client.patch<ResourceContent>(`/resources/${resource.id}/status`, { status });
      toast.success(`${resource.title} ${RESOURCE_STATUS_LABELS[status].toLowerCase()}`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update status");
    }
  };

  const confirmPublish = async () => {
    // Second guard behind the disabled button: a double-fire (keyboard + click)
    // can't queue a second request.
    if (!publishTarget || publishing) return;

    setPublishing(true);
    try {
      await client.patch<ResourceContent>(`/resources/${publishTarget.id}/status`, {
        status: "published",
      });
      setPublishTarget(null);
      toast.success("Content published successfully.");
      load();
    } catch (err) {
      // Leave the dialog open and the row untouched — only a confirmed success
      // response is allowed to report the content as published.
      toast.error(
        err instanceof ApiError ? err.message : "Unable to publish the content. Please try again.",
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async (resource: ResourceContent) => {
    if (!confirm(`Delete "${resource.title}"? This cannot be undone.`)) return;
    try {
      await client.delete(`/resources/${resource.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    }
  };

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (resource: ResourceContent) => { setEditing(resource); setModalOpen(true); };

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin"
        title="Resource Content Management"
        description="Create and publish the Glossary, FAQ, News, and Mastermind content shown across the platform."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              // Opens on the library the grid is filtered to, when it has categories.
              onClick={() =>
                setCategoryManagerType(
                  typeFilter && RESOURCE_TYPES_WITH_CATEGORIES.includes(typeFilter)
                    ? typeFilter
                    : RESOURCE_TYPES_WITH_CATEGORIES[0],
                )
              }
              className="gap-2"
            >
              <Library className="h-4 w-4" />
              Manage Categories
            </Button>
            <Button onClick={openCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Resource
            </Button>
          </div>
        }
        aside={
          <div>
            <h2 className="text-lg font-bold text-foreground">Library snapshot</h2>
            <div className="mt-3 grid grid-cols-2 gap-2.5">
              {RESOURCE_TYPES.map((t) => (
                <div key={t} className="public-glass-card rounded-xl p-3">
                  <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    {t === "news" ? "News" : RESOURCE_TYPE_LABELS[t]}
                  </div>
                  <div className="mt-1.5 text-xl font-extrabold text-foreground">{counts[t] ?? 0}</div>
                </div>
              ))}
            </div>
          </div>
        }
      />

      {/* Filters */}
      <PagePanel>
        <div className="mb-4">
          <h2 className="text-base font-bold text-foreground">Search &amp; filter</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Find content by title, body, or category — then narrow by type and status.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Search
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-10 pl-9 text-sm"
                placeholder="Search title, content, category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Resource Type
            </label>
            <select
              className={`${SELECT_CLASS} h-10`}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as "" | ResourceType)}
            >
              <option value="">All</option>
              {RESOURCE_TYPES.map((t) => (
                <option key={t} value={t}>{RESOURCE_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </label>
            <select
              className={`${SELECT_CLASS} h-10`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "" | ResourceStatus)}
            >
              <option value="">All</option>
              {RESOURCE_STATUSES.map((s) => (
                <option key={s} value={s}>{RESOURCE_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <Button type="button" onClick={applySearch} className="h-10 gap-2 rounded-full px-5">
            <Search className="h-3.5 w-3.5" />
            Search
          </Button>
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </PagePanel>

      {/* Grid */}
      <PagePanel>
        <PanelHeading
          title="All Resources"
          description={data ? `${total} record${total !== 1 ? "s" : ""} ${hasFilters ? "found" : "total"}` : undefined}
        />

        {loading ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : items.length === 0 ? (
          <EmptyPanel
            icon={Library}
            title={hasFilters ? "No matching resources" : "No resources yet"}
            description={
              hasFilters
                ? "Nothing matches those filters. Clear them to see the full library."
                : "Add your first Glossary term, FAQ, article, or Mastermind item to get started."
            }
          />
        ) : (
          <div>
            <div className="-mx-5 overflow-x-auto sm:-mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/30">
                    <th className="py-3 pl-5 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:pl-6">Title</th>
                    <th className="hidden px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell">Resource Type</th>
                    <th className="hidden px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground lg:table-cell">Available For</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                    <th className="hidden whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground md:table-cell">Publish Date</th>
                    <th className="py-3 pl-3 pr-5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {items.map((r) => (
                    <tr key={r.id} className="align-middle transition-colors hover:bg-muted/25">
                      <td className="min-w-[220px] py-3.5 pl-5 pr-3 sm:pl-6">
                        {/* The title is the preview affordance as well as the
                            View button — the whole row reads as one target. */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <button
                            type="button"
                            onClick={() => setViewing(r)}
                            title={`View ${r.title}`}
                            className="text-left font-bold text-foreground transition-colors hover:text-primary hover:underline underline-offset-2"
                          >
                            {r.title}
                          </button>
                          {r.category && (
                            <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 text-[0.68rem] font-medium text-muted-foreground">
                              {r.category}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground sm:hidden">
                          {RESOURCE_TYPE_LABELS[r.resource_type]}
                        </p>
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-3.5 text-muted-foreground sm:table-cell">
                        {RESOURCE_TYPE_LABELS[r.resource_type]}
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-3.5 lg:table-cell">
                        <span className="inline-flex items-center rounded-full border border-primary/15 bg-primary/5 px-2 py-0.5 text-[0.68rem] font-medium text-primary">
                          {formatRoleLabels(r.role_ids, roles)}
                        </span>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_PILL_CLASS[r.status]}`}>
                          {RESOURCE_STATUS_LABELS[r.status]}
                        </span>
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-3.5 text-xs text-muted-foreground md:table-cell">
                        {formatPublishedDate(r.published_at)}
                      </td>
                      <td className="whitespace-nowrap py-3.5 pl-3 pr-5 sm:pr-6">
                        {/* flex-nowrap, not wrap: five controls used to spill onto
                            a second line and leave a lone delete icon dangling
                            under the row. The table already scrolls sideways. */}
                        <div className="flex flex-nowrap items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setViewing(r)}
                            title={`View ${r.title}`}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
                          >
                            <BookOpen className="h-3 w-3" />
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(r)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
                          >
                            <Pencil className="h-3 w-3" />
                            Edit
                          </button>
                          {r.status === "published" ? (
                            <button
                              type="button"
                              onClick={() => changeStatus(r, "draft")}
                              title="Unpublish"
                              className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
                            >
                              <EyeOff className="h-3 w-3" />
                              Unpublish
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setPublishTarget(r)}
                              title="Publish"
                              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-xs font-medium text-emerald-600 shadow-sm transition-all hover:bg-emerald-500/10"
                            >
                              <Eye className="h-3 w-3" />
                              Publish
                            </button>
                          )}
                          {r.status !== "archived" && (
                            <button
                              type="button"
                              onClick={() => changeStatus(r, "archived")}
                              aria-label={`Archive ${r.title}`}
                              title="Archive"
                              className="inline-flex items-center rounded-lg border border-border/70 bg-background p-1.5 text-muted-foreground shadow-sm transition-all hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-600"
                            >
                              <Archive className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDelete(r)}
                            aria-label={`Delete ${r.title}`}
                            title="Delete"
                            className="inline-flex items-center rounded-lg border border-border/70 bg-background p-1.5 text-muted-foreground shadow-sm transition-all hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalElements={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              className="mt-1 px-5 sm:px-6"
            />
          </div>
        )}
      </PagePanel>

      {modalOpen && (
        <ResourceFormModal
          client={client}
          editing={editing}
          roles={roles}
          categories={categories}
          onManageCategories={(t) => setCategoryManagerType(t)}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
        />
      )}

      {categoryManagerType && (
        <CategoryManagerModal
          // Remount per type so one library's list and in-progress rename never
          // carry over into another's.
          key={categoryManagerType}
          client={client}
          resourceType={categoryManagerType}
          onResourceTypeChange={setCategoryManagerType}
          onClose={() => setCategoryManagerType(null)}
          // Refreshing the grid too: a rename rewrites the category shown on
          // every entry filed under it.
          onChanged={(rows) => { replaceCategories(categoryManagerType, rows); load(); }}
        />
      )}

      {viewing && (
        <ResourceViewModal
          resource={viewing}
          roles={roles}
          showReaderLink={client === api}
          onClose={() => setViewing(null)}
          onEdit={() => { const r = viewing; setViewing(null); openEdit(r); }}
        />
      )}

      <PublishConfirmationModal
        isOpen={!!publishTarget}
        resourceName={publishTarget?.title}
        resourceType={publishTarget ? RESOURCE_TYPE_LABELS[publishTarget.resource_type] : null}
        currentStatus={publishTarget ? RESOURCE_STATUS_LABELS[publishTarget.status] : null}
        targetAudience={publishTarget ? formatRoleLabels(publishTarget.role_ids, roles) : null}
        isLoading={publishing}
        onConfirm={confirmPublish}
        onCancel={() => { if (!publishing) setPublishTarget(null); }}
      />
    </PageShell>
  );
}
